import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';

// ═══════════════════════════════════════════════════════════════
// MAINTENANCE GATE
// ═══════════════════════════════════════════════════════════════
// Wraps the app's routing tree. When config/system.maintenanceMode is true,
// shows a "coming soon" page to all non-admin visitors. Admins and superAdmins
// can still access the full app for testing.
//
// /admin-pin and /login remain accessible so admins can sign in even when gated.
// ═══════════════════════════════════════════════════════════════
export default function MaintenanceGate({ children }) {
  const { currentUser, userProfile, loading: authLoading } = useAuth();
  const [maintenanceMode, setMaintenanceMode] = useState(null); // null = loading
  const [config, setConfig] = useState(null);

  useEffect(() => {
    let active = true;
    getDoc(doc(db, 'config', 'system'))
      .then((snap) => {
        if (!active) return;
        const data = snap.exists() ? snap.data() : {};
        setMaintenanceMode(!!data.maintenanceMode);
        setConfig(data);
      })
      .catch(() => {
        if (active) {
          // If we can't read config (rules error, network), default to NOT maintenance.
          // Better to leave the app open than to lock everyone out due to a transient failure.
          setMaintenanceMode(false);
        }
      });
    return () => { active = false; };
  }, []);

  // Bypass: routes that must always be reachable so admins can sign in to disable maintenance
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const alwaysOpen = ['/login', '/admin-pin', '/legal', '/register', '/driver/register', '/verify-otp', '/forgot-password', '/reset-password']
    .some((p) => path === p || path.startsWith(`${p}/`));

  // Loading state
  if (maintenanceMode === null || authLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100vh', color: '#94a3b8' }}>
        Loading...
      </div>
    );
  }

  // Signed-in users bypass the gate (testers, drivers, riders, admins). Public visitors
  // remain on the coming-soon page. (At launch, set maintenanceMode=false to open fully.)
  if (currentUser) return children;

  // Always-open paths bypass
  if (alwaysOpen) return children;

  // Maintenance mode: show coming-soon
  if (maintenanceMode) {
    return <ComingSoonPage config={config} />;
  }

  return children;
}

// ═══════════════════════════════════════════════════════════════
// COMING SOON PAGE
// ═══════════════════════════════════════════════════════════════
function ComingSoonPage({ config }) {
  return (
    <div className="flex flex-col" style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #faf8f5 0%, #f1f5f9 100%)' }}>
      <div className="flex-1 w-full mx-auto" style={{ maxWidth: 720, padding: '60px 24px 40px' }}>
        <div className="flex items-center gap-3" style={{ marginBottom: 48 }}>
          <div className="flex items-center justify-center font-extrabold text-brand-dark" style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'linear-gradient(135deg, #2dd4a8, #6ee7b7)',
            fontSize: 18, boxShadow: '0 4px 15px rgba(45,212,168,0.3)',
          }}>R</div>
          <span className="font-serif text-brand-dark" style={{ fontSize: '1.5rem' }}>Reservo</span>
        </div>

        <h1 className="font-serif text-brand-dark" style={{
          fontSize: 'clamp(2rem, 4vw, 3rem)', margin: '0 0 16px', letterSpacing: '-0.02em', lineHeight: 1.15,
        }}>Pre-booked rides, pre-paid certainty.</h1>

        <p className="text-body" style={{ fontSize: '1.05rem', lineHeight: 1.6, margin: '0 0 28px', maxWidth: 560 }}>
          Reservo is a NSW point-to-point pre-booked ride service. Lock in your driver and your fare,
          1 hour to 30 days in advance. No surge pricing. No surprise fares.
        </p>

        <div className="inline-flex items-center gap-2 bg-white text-body" style={{
          padding: '8px 16px', borderRadius: 100, border: '1px solid #e2e8f0', fontSize: '0.85rem', marginBottom: 48,
        }}>
          <span className="rounded-full" style={{ width: 8, height: 8, background: '#fbbf24', boxShadow: '0 0 0 4px rgba(251,191,36,0.2)' }} />
          <span>Launching soon — currently in pre-launch testing</span>
        </div>

        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 48 }}>
          <Feature icon="📅" title="Pre-booked" desc="Schedule up to 30 days ahead" />
          <Feature icon="💰" title="Upfront pricing" desc="Know the fare before you book" />
          <Feature icon="⭐" title="Choose your driver" desc="Pick from approved drivers in your area" />
          <Feature icon="🛡️" title="NSW-licensed" desc="Fully compliant point-to-point service" />
        </div>

        <div className="bg-white" style={{ padding: 20, borderRadius: 12, border: '1px solid #e2e8f0', marginBottom: 24 }}>
          <div className="font-bold uppercase" style={{ fontSize: '0.78rem', color: '#94a3b8', letterSpacing: 1, marginBottom: 6 }}>Contact</div>
          <a href="mailto:admin@reservo.au" className="text-brand-primary font-semibold no-underline" style={{ fontSize: '1rem' }}>admin@reservo.au</a>
        </div>

        <div className="text-center" style={{ marginTop: 32 }}>
          <Link to="/login" style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Staff login</Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="bg-white" style={{ padding: 18, borderRadius: 14, border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: '1.6rem', marginBottom: 6 }}>{icon}</div>
      <div className="font-bold text-brand-dark" style={{ fontSize: '0.92rem', marginBottom: 2 }}>{title}</div>
      <div className="text-body" style={{ fontSize: '0.78rem' }}>{desc}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FOOTER (used both on coming-soon page and as shared component)
// ═══════════════════════════════════════════════════════════════
export function Footer() {
  return (
    <footer style={{ background: '#0a1628', padding: '32px 24px', color: '#94a3b8' }}>
      <div className="mx-auto flex flex-col gap-3" style={{ maxWidth: 1100 }}>
        <div style={{ fontSize: '0.92rem' }}>
          <strong style={{ color: '#0a1628' }}>Reservo</strong> — NSW pre-booked point-to-point ride service
        </div>
        <div className="flex flex-wrap" style={{ gap: 16 }}>
          <Link to="/legal" className="text-brand-primary font-semibold no-underline" style={{ fontSize: '0.85rem' }}>Terms & Privacy</Link>
          <a href="mailto:admin@reservo.au" className="text-brand-primary font-semibold no-underline" style={{ fontSize: '0.85rem' }}>admin@reservo.au</a>
        </div>
        <div style={{ fontSize: '0.78rem', color: '#475569', paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          © {new Date().getFullYear()} Reservo · ABN 50639154277 · Registered business name in NSW, Australia
        </div>
      </div>
    </footer>
  );
}